import Vue from 'vue';
import Keycloak from 'keycloak-js';
import jwt from 'jwt-decode';

// Keep the adapter outside Vue's reactive state.
const keycloak = new Keycloak({
    url: process.env.VUE_APP_IAM_AUTH_SERVER_URL,
    realm: process.env.VUE_APP_IAM_REALM,
    clientId: process.env.VUE_APP_IAM_CLIENT_ID,
});
let signInPromise;
let refreshPromise;
let refreshTimer;
let session = 0;

const auth = new Vue({
    data: () => ({
        isAuthenticated: false,
        user: null,
        groups: [],
        resourceAccess: {},
        error: null,
    }),
    methods: {
        init() {
            return this.signIn();
        },
        signIn() {
            if (!signInPromise) {
                signInPromise = Promise.resolve().then(() => keycloak.init({
                    onLoad: 'login-required',
                    checkLoginIframe: false,
                    responseMode: 'query',
                    flow: 'standard',
                    pkceMethod: 'S256',
                })).then(async authenticated => {
                    if (!authenticated) throw new Error('Authentication failed');
                    await this.updateUser();
                    this.isAuthenticated = true;
                    refreshTimer = setInterval(() => {
                        // refreshToken updates the error state before rejecting.
                        this.refreshToken(60).catch(() => {});
                    }, 3000);
                }).catch(error => {
                    this.fail('Unable to sign in. Please try again.');
                    throw error;
                });
            }
            return signInPromise;
        },
        resetSession() {
            session += 1;
            clearInterval(refreshTimer);
            this.isAuthenticated = false;
            this.user = null;
            this.groups = [];
            this.resourceAccess = {};
        },
        fail(message) {
            this.resetSession();
            this.error = message;
            keycloak.clearToken();
        },
        async logout() {
            this.resetSession();
            try {
                // Let the adapter use the ID token and construct the logout URL.
                await keycloak.logout({ redirectUri: window.location.origin + '/' });
            } catch (error) {
                this.fail('Unable to sign out. Please try again.');
            }
        },
        getToken() {
            return this.isAuthenticated ? keycloak.token : undefined;
        },
        async updateUser() {
            const currentSession = session;
            const userInfo = await keycloak.loadUserInfo();
            if (currentSession !== session) return;
            this.groups = userInfo.groups || [];
            this.user = userInfo;
            this.resourceAccess = keycloak.resourceAccess || {};
        },
        refreshToken(minValidity = -1) {
            if (!this.isAuthenticated) return Promise.reject(new Error('Not authenticated'));
            if (!refreshPromise) {
                const currentSession = session;
                refreshPromise = Promise.resolve().then(() => keycloak.updateToken(minValidity))
                    .then(async refreshed => {
                        if (currentSession !== session) throw new Error('Session ended');
                        if (refreshed) await this.updateUser();
                        if (currentSession !== session) throw new Error('Session ended');
                        return refreshed;
                    }).catch(error => {
                        if (currentSession === session) {
                            this.fail('Your session could not be renewed. Please sign in again.');
                        }
                        throw error;
                    }).finally(() => { refreshPromise = null; });
            }
            return refreshPromise;
        },
        isExpiring(token) {
            return jwt(token).exp - Math.floor(Date.now() / 1000) < 60;
        },
        getUserRoles() {
            return (this.user && this.user.realmRoles) || [];
        },
    },
});

export default {
    install(Vue) {
        Vue.prototype.$auth = auth;
    },
};
